import type { IconName } from './Icon';
import type { ToneName } from './tones';

/**
 * O icone e a cor de cada acao de linha, num lugar so (`AcaoComIcone`).
 *
 * A cor vem do sentido, com os tons de `tones.ts`:
 *
 * - ver e abrir (visualizar, analisar, ver texto, consultar): a cor da marca;
 * - o que apaga, bloqueia, recusa ou encerra: vermelho (`danger`);
 * - aprovar, ativar, desbloquear e restaurar: verde (`success`);
 * - o que pausa ou desliga sem apagar, e o que marca como principal: amarelo
 *   (`warning`);
 * - Word em azul (`info`) e PDF em vermelho, as cores com que as pessoas reconhecem os
 *   formatos;
 * - as outras acoes neutras se separam por categoria (`violet` para editar e
 *   configurar, `indigo` para imprimir e baixar, `cyan` para copiar, enviar e o
 *   historico, `teal` para assinar e decidir). A categoria so separa uma acao da outra
 *   na mesma linha, e nao diz se algo esta bem ou mal.
 *
 * A cor nunca e o unico sinal: a dica e o nome acessivel dizem a acao.
 */
export type TipoDeAcao =
  | 'visualizar'
  | 'abrir'
  | 'analisar'
  | 'consultar'
  | 'verTexto'
  | 'verItens'
  | 'abrirFora'
  | 'imprimir'
  | 'baixar'
  | 'exportar'
  | 'enviarArquivo'
  | 'baixarWord'
  | 'baixarPdf'
  | 'editar'
  | 'renomear'
  | 'configurar'
  | 'anotar'
  | 'excluir'
  | 'remover'
  | 'bloquear'
  | 'desbloquear'
  | 'aprovar'
  | 'recusar'
  | 'cancelar'
  | 'revogar'
  | 'retirar'
  | 'ativar'
  | 'desativar'
  | 'suspender'
  | 'reativar'
  | 'restaurar'
  | 'desfazer'
  | 'executar'
  | 'copiar'
  | 'historico'
  | 'reenviar'
  | 'enviar'
  | 'email'
  | 'vincular'
  | 'anexar'
  | 'assinar'
  | 'decidir'
  | 'boleto'
  | 'acessarComo'
  | 'adicionar'
  | 'tornarPrincipal'
  | 'subir'
  | 'descer'
  | 'refazer';

export interface AparenciaDaAcao {
  icone: IconName;
  tom: ToneName;
}

export const tiposDeAcao: Record<TipoDeAcao, AparenciaDaAcao> = {
  visualizar: { icone: 'eye', tom: 'brand' },
  abrir: { icone: 'eye', tom: 'brand' },
  analisar: { icone: 'search', tom: 'brand' },
  consultar: { icone: 'search', tom: 'brand' },
  verTexto: { icone: 'fileText', tom: 'brand' },
  verItens: { icone: 'list', tom: 'brand' },
  abrirFora: { icone: 'external', tom: 'brand' },
  imprimir: { icone: 'printer', tom: 'indigo' },
  baixar: { icone: 'download', tom: 'indigo' },
  exportar: { icone: 'download', tom: 'indigo' },
  enviarArquivo: { icone: 'upload', tom: 'indigo' },
  baixarWord: { icone: 'word', tom: 'info' },
  baixarPdf: { icone: 'pdf', tom: 'danger' },
  editar: { icone: 'pencil', tom: 'violet' },
  renomear: { icone: 'pencil', tom: 'violet' },
  configurar: { icone: 'settings', tom: 'violet' },
  anotar: { icone: 'note', tom: 'violet' },
  excluir: { icone: 'trash', tom: 'danger' },
  remover: { icone: 'trash', tom: 'danger' },
  bloquear: { icone: 'lock', tom: 'danger' },
  desbloquear: { icone: 'lockOpen', tom: 'success' },
  aprovar: { icone: 'check', tom: 'success' },
  recusar: { icone: 'close', tom: 'danger' },
  cancelar: { icone: 'ban', tom: 'danger' },
  revogar: { icone: 'ban', tom: 'danger' },
  retirar: { icone: 'userMinus', tom: 'danger' },
  ativar: { icone: 'power', tom: 'success' },
  desativar: { icone: 'power', tom: 'warning' },
  suspender: { icone: 'pause', tom: 'warning' },
  reativar: { icone: 'play', tom: 'success' },
  restaurar: { icone: 'undo', tom: 'success' },
  desfazer: { icone: 'undo', tom: 'neutral' },
  executar: { icone: 'play', tom: 'success' },
  copiar: { icone: 'copy', tom: 'cyan' },
  historico: { icone: 'history', tom: 'cyan' },
  reenviar: { icone: 'resend', tom: 'cyan' },
  enviar: { icone: 'send', tom: 'cyan' },
  email: { icone: 'mail', tom: 'cyan' },
  vincular: { icone: 'link', tom: 'cyan' },
  anexar: { icone: 'paperclip', tom: 'cyan' },
  assinar: { icone: 'signature', tom: 'teal' },
  decidir: { icone: 'gavel', tom: 'teal' },
  boleto: { icone: 'receipt', tom: 'teal' },
  acessarComo: { icone: 'login', tom: 'teal' },
  adicionar: { icone: 'plus', tom: 'brand' },
  tornarPrincipal: { icone: 'star', tom: 'warning' },
  subir: { icone: 'arrowUp', tom: 'neutral' },
  descer: { icone: 'arrowDown', tom: 'neutral' },
  refazer: { icone: 'refresh', tom: 'cyan' },
};
